/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { FC, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
// eslint-disable-next-line import/no-duplicates
import sub from "date-fns/sub";
// eslint-disable-next-line import/no-duplicates
import { enGB, ru } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  Box,
  Button,
  ClickAwayListener,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { AccessTime, ExpandMore, CheckOutlined } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS, ruRU } from "@mui/x-date-pickers/locales";

import { parseUTCStringAsLocal, RangeType, useAsu } from "@framework/provider-asu";
import {
  useUser,
  SelfRecorderMode,
  SelfRecorderRangeEnum,
  ISetSelfRecorderSettingsDto,
} from "@framework/provider-user";
import { IUser } from "@framework/types";
import { useApiCall } from "@framework/react-hooks";

import DateTimeIcon from "../../../../assets/icons/date-time.svg";
import { StyledInputsWrapper } from "./styled";

registerLocale("RU", ru);
registerLocale("EN", enGB);

const DATE_TIME_FORMAT_WITH_SECONDS = "dd.MM.yyyy HH:mm:ss";
const DATE_RANGE_INPUT_MIN_WIDTH = "188px";
const TIME_INPUT_WITH_SECONDS_WIDTH = "110px";
const TIME_PICKER_OPTION_HEIGHT = 28;
const TIME_PICKER_COLUMN_HEIGHT = TIME_PICKER_OPTION_HEIGHT * 5;
const TIME_PICKER_COLUMN_WIDTH = "3rem";
const MIN_SELECTABLE_DATE = new Date(1990, 1, 1);
const TIME_WITH_OPTIONAL_SECONDS_REGEXP = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;
const TIME_PART_SELECTION_RANGE = {
  hours: [0, 2],
  minutes: [3, 5],
  seconds: [6, 8],
} as const;

type TimePart = keyof typeof TIME_PART_SELECTION_RANGE;

const TIME_PART_LIMITS: Record<TimePart, number> = {
  hours: 23,
  minutes: 59,
  seconds: 59,
};
const HOURS_OPTIONS = Array.from({ length: 24 }, (_, value) => value);
const MINUTES_SECONDS_OPTIONS = Array.from({ length: 60 }, (_, value) => value);
const TIME_PART_OPTIONS: Record<TimePart, number[]> = {
  hours: HOURS_OPTIONS,
  minutes: MINUTES_SECONDS_OPTIONS,
  seconds: MINUTES_SECONDS_OPTIONS,
};

const isValidDate = (date?: Date): date is Date => Boolean(date && !Number.isNaN(date.getTime()));

const padTimeUnit = (value: number): string => value.toString().padStart(2, "0");

const formatTimeWithSeconds = (date?: Date): string => {
  if (!isValidDate(date)) return "";

  return [date.getHours(), date.getMinutes(), date.getSeconds()].map(padTimeUnit).join(":");
};

const parseTimeWithOptionalSeconds = (value: string): { hours: number; minutes: number; seconds: number } | null => {
  const match = TIME_WITH_OPTIONAL_SECONDS_REGEXP.exec(value);

  if (!match) return null;

  const [, hours, minutes, seconds = "00"] = match;
  const timeParts = {
    hours: Number.parseInt(hours, 10),
    minutes: Number.parseInt(minutes, 10),
    seconds: Number.parseInt(seconds, 10),
  };

  const isValidTime =
    timeParts.hours >= 0 &&
    timeParts.hours <= 23 &&
    timeParts.minutes >= 0 &&
    timeParts.minutes <= 59 &&
    timeParts.seconds >= 0 &&
    timeParts.seconds <= 59;

  return isValidTime ? timeParts : null;
};

const applyTimeWithSeconds = (date: Date | undefined, value: string): Date | null => {
  const nextTime = parseTimeWithOptionalSeconds(value);

  if (!nextTime) return null;

  const nextDate = isValidDate(date) ? new Date(date.getTime()) : new Date();
  nextDate.setHours(nextTime.hours, nextTime.minutes, nextTime.seconds, 0);

  return nextDate;
};

const getTimePartByCursor = (input: HTMLInputElement): TimePart => {
  const cursorPosition = input.selectionStart ?? input.value.length;

  if (cursorPosition <= TIME_PART_SELECTION_RANGE.hours[1]) return "hours";
  if (cursorPosition <= TIME_PART_SELECTION_RANGE.minutes[1]) return "minutes";

  return "seconds";
};

const selectTimePart = (input: HTMLInputElement, timePart: TimePart): void => {
  const [start, end] = TIME_PART_SELECTION_RANGE[timePart];
  input.setSelectionRange(start, end);
};

const stepTimePartWithoutCarry = (value: string, timePart: TimePart, direction: 1 | -1): string | null => {
  const timeParts = parseTimeWithOptionalSeconds(value);

  if (!timeParts) return null;

  const limit = TIME_PART_LIMITS[timePart];
  const nextValue = timeParts[timePart] + direction;
  timeParts[timePart] = nextValue < 0 ? limit : nextValue > limit ? 0 : nextValue;

  return [timeParts.hours, timeParts.minutes, timeParts.seconds].map(padTimeUnit).join(":");
};

const setTimePartValue = (value: string, timePart: TimePart, nextPartValue: number): string | null => {
  const timeParts = parseTimeWithOptionalSeconds(value);

  if (!timeParts) return null;

  timeParts[timePart] = nextPartValue;

  return [timeParts.hours, timeParts.minutes, timeParts.seconds].map(padTimeUnit).join(":");
};

const datepickerToFakeUtcISO = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();

interface ITimeInputWithSecondsProps {
  ariaLabel: string;
  date?: Date;
  onDateChange: (date: Date) => void;
}

const TimeInputWithSeconds: FC<ITimeInputWithSecondsProps> = ({ ariaLabel, date, onDateChange }) => {
  const timeValue = formatTimeWithSeconds(date);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timePickerColumnRefs = useRef<Record<TimePart, HTMLDivElement | null>>({
    hours: null,
    minutes: null,
    seconds: null,
  });
  const pendingValueRef = useRef<string | null>(null);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState(timeValue);

  useEffect(() => {
    pendingValueRef.current = null;
    if (inputRef.current) {
      inputRef.current.value = timeValue;
    }
    if (!isTimePickerOpen) {
      setPickerValue(timeValue);
    }
  }, [isTimePickerOpen, timeValue]);

  useEffect(() => {
    if (!isTimePickerOpen) return;

    const timeParts = parseTimeWithOptionalSeconds(pickerValue);

    if (!timeParts) return;

    (Object.keys(TIME_PART_SELECTION_RANGE) as TimePart[]).forEach(timePart => {
      const column = timePickerColumnRefs.current[timePart];

      if (!column) return;

      column.scrollTop = Math.max(timeParts[timePart] * TIME_PICKER_OPTION_HEIGHT - TIME_PICKER_OPTION_HEIGHT * 2, 0);
    });
  }, [isTimePickerOpen, pickerValue]);

  const getActiveTimeValue = useCallback(() => {
    const value = pendingValueRef.current ?? inputRef.current?.value ?? timeValue;

    return parseTimeWithOptionalSeconds(value) ? value : timeValue || "00:00:00";
  }, [timeValue]);

  const commitTimeValue = useCallback(
    (value: string): boolean => {
      const nextDate = applyTimeWithSeconds(date, value);

      pendingValueRef.current = null;

      if (nextDate) {
        onDateChange(nextDate);
        return true;
      }

      if (inputRef.current) {
        inputRef.current.value = timeValue;
      }

      return false;
    },
    [date, onDateChange, timeValue],
  );

  const commitDraftValue = useCallback(() => {
    commitTimeValue(getActiveTimeValue());
  }, [commitTimeValue, getActiveTimeValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    pendingValueRef.current = value;
  };

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    selectTimePart(input, getTimePartByCursor(input));
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    selectTimePart(input, getTimePartByCursor(input));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

    event.preventDefault();

    const input = event.currentTarget;
    const timePart = getTimePartByCursor(input);
    const sourceValue = pendingValueRef.current ?? input.value;
    const direction = event.key === "ArrowUp" ? 1 : -1;
    const nextValue =
      stepTimePartWithoutCarry(sourceValue, timePart, direction) ??
      stepTimePartWithoutCarry(timeValue || "00:00:00", timePart, direction);

    if (!nextValue) return;

    input.value = nextValue;
    pendingValueRef.current = nextValue;
    if (isTimePickerOpen) {
      setPickerValue(nextValue);
    }
    selectTimePart(input, timePart);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown" || event.key === "Enter") {
      commitDraftValue();
    }
  };

  const handleTimePickerToggle = () => {
    const nextValue = getActiveTimeValue();

    setPickerValue(nextValue);
    setIsTimePickerOpen(value => !value);
  };

  const handleTimePartSelect = (timePart: TimePart, value: number) => {
    const nextValue =
      setTimePartValue(pickerValue, timePart, value) ?? setTimePartValue(getActiveTimeValue(), timePart, value);

    if (!nextValue) return;

    if (inputRef.current) {
      inputRef.current.value = nextValue;
    }
    pendingValueRef.current = nextValue;
    setPickerValue(nextValue);
    commitTimeValue(nextValue);
  };

  const pickerTimeParts = parseTimeWithOptionalSeconds(pickerValue) ?? parseTimeWithOptionalSeconds(timeValue);

  const renderTimePickerColumn = (timePart: TimePart) => (
    <Box
      key={timePart}
      ref={(node: HTMLDivElement | null) => {
        timePickerColumnRefs.current[timePart] = node;
      }}
      sx={{
        maxHeight: TIME_PICKER_COLUMN_HEIGHT,
        overflowX: "hidden",
        overflowY: "auto",
        px: 0.25,
        scrollbarColor: "rgba(0, 0, 0, 0.28) transparent",
        scrollbarWidth: "thin",
        width: TIME_PICKER_COLUMN_WIDTH,
        "&::-webkit-scrollbar": {
          width: 2,
        },
        "&::-webkit-scrollbar-track": {
          bgcolor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: "action.disabled",
          borderRadius: 2,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          bgcolor: "text.disabled",
        },
      }}
    >
      {TIME_PART_OPTIONS[timePart].map(value => {
        const selected = pickerTimeParts?.[timePart] === value;

        return (
          <Box
            aria-pressed={selected}
            component="button"
            key={value}
            onClick={() => handleTimePartSelect(timePart, value)}
            sx={{
              bgcolor: selected ? "#216ba5" : "transparent",
              border: 0,
              borderRadius: selected ? ".3rem" : 0,
              boxSizing: "border-box",
              color: selected ? "#fff" : "text.primary",
              cursor: "pointer",
              display: "block",
              fontFamily: "Helvetica Neue, helvetica, arial, sans-serif",
              fontSize: ".8rem",
              fontWeight: selected ? 700 : 400,
              height: "1.7rem",
              lineHeight: "1.7rem",
              m: "0 auto",
              p: 0,
              textAlign: "center",
              width: "1.7rem",
              "&:hover": {
                bgcolor: selected ? "#216ba5" : "action.hover",
              },
            }}
            type="button"
          >
            {padTimeUnit(value)}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={() => setIsTimePickerOpen(false)}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          position: "relative",
          width: TIME_INPUT_WITH_SECONDS_WIDTH,
        }}
      >
        <input
          aria-label={ariaLabel}
          className="react-datepicker-time__input"
          defaultValue={timeValue}
          inputMode="numeric"
          maxLength={8}
          name="time-input"
          onBlur={commitDraftValue}
          onChange={handleChange}
          onClick={handleClick}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
          ref={inputRef}
          required
          style={{
            boxSizing: "border-box",
            font: "inherit",
            height: 24,
            lineHeight: "20px",
            paddingLeft: 4,
            paddingRight: 24,
            width: TIME_INPUT_WITH_SECONDS_WIDTH,
          }}
          type="text"
        />
        <IconButton
          aria-label={ariaLabel}
          onClick={handleTimePickerToggle}
          size="small"
          sx={{
            height: 22,
            p: 0,
            position: "absolute",
            right: 1,
            top: "50%",
            transform: "translateY(-50%)",
            width: 22,
          }}
        >
          <AccessTime sx={{ fontSize: 16 }} />
        </IconButton>
        {isTimePickerOpen ? (
          <Box
            sx={{
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: 3,
              display: "flex",
              gap: 0.25,
              overflowX: "hidden",
              p: 0.5,
              position: "absolute",
              right: 0,
              top: "calc(100% + 4px)",
              zIndex: theme => theme.zIndex.modal,
            }}
          >
            {(["hours", "minutes", "seconds"] as TimePart[]).map(renderTimePickerColumn)}
          </Box>
        ) : null}
      </Box>
    </ClickAwayListener>
  );
};

interface IDateRangeInputProps {
  className?: string;
  label: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  value?: string;
}

const DateRangeInput = forwardRef<HTMLInputElement, IDateRangeInputProps>(
  ({ value, onClick, onChange, className, label }, ref) => {
    return (
      <TextField
        inputRef={ref}
        className={className}
        value={value}
        onClick={onClick}
        onChange={onChange}
        size="small"
        label={label}
        sx={{ minWidth: DATE_RANGE_INPUT_MIN_WIDTH }}
      />
    );
  },
);

DateRangeInput.displayName = "DateRangeInput";

export const DateRange: FC = () => {
  const {
    startTimestamp,
    endTimestamp,
    rangeType,
    updateTimestamps,
    updateTimestampsFakeUTC,
    updateQS,
    userSelectedRecorderModePeriod,
    setUserSelectedRecorderModePeriod,
    recorderModeInterval,
    setRecorderModeInterval,
    userSelectedRangeType,
    userSelectedStartTimestamp,
    userSelectedEndTimestamp,
    changeUserSelectedRangeType,
    setUserSelectedStartTimestamp,
    setUserSelectedEndTimestamp,
    setIsJournalNavigation,
    manualRangeType,
    setManualRangeType,
    setJournalTimeRangeHistory,
    skipNextJournalReload,
    setSkipNextJournalReload,
    justRestoredRange,
    setJustRestoredRange,
    tab,
    recorderWindowStart,
    recorderWindowEnd,
    isRecorderModeActive,
    isRecorderModeDraftDirty,
    startRecorderMode,
    stopRecorderMode,
  } = useAsu();

  const user = useUser<IUser>();

  const endDatePickerRef = useRef<any>(null);

  const [shouldOpenEndPicker, setShouldOpenEndPicker] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const { formatMessage, locale } = useIntl();
  const startDateRangeLabel = formatMessage({ id: "form.labels.dateRangeStart" });
  const endDateRangeLabel = formatMessage({ id: "form.labels.dateRangeEnd" });
  const timeLabel = formatMessage({ id: "components.datetime.time" });

  const showRecorderModeInputs = userSelectedRangeType === RangeType.RECORDER_MODE;
  const showDateRange = userSelectedRangeType === RangeType.SELECTED_RANGE;

  const disabledRecorderModeTabs = ["4"];
  const isRecorderModeDisabled = disabledRecorderModeTabs.includes(tab);
  const isJournalTab = tab === "2";
  const recorderModePeriodOptions = Object.keys(SelfRecorderRangeEnum).filter(
    key => isNaN(+key) && !(isJournalTab && ["seconds", "minutes"].includes(key)),
  );
  const selectedRecorderModePeriod =
    isJournalTab && ["seconds", "minutes"].includes(userSelectedRecorderModePeriod)
      ? "hours"
      : userSelectedRecorderModePeriod;

  const recorderMode =
    (user.profile?.selfRecorderSettings as { mode?: SelfRecorderMode } | undefined)?.mode ?? SelfRecorderMode.INTERVAL;
  const recorderModeMinInterval =
    userSelectedRecorderModePeriod === "seconds" && recorderMode === SelfRecorderMode.INTERVAL
      ? Math.max(1, user.profile?.selfRecorderSettings?.upd ?? 1)
      : 1;
  const isRecorderModeDraftValid =
    Boolean(userSelectedRecorderModePeriod) &&
    recorderModeInterval >= recorderModeMinInterval &&
    recorderModeInterval <= 9999;
  const recorderModeValidationMessage =
    userSelectedRecorderModePeriod === "seconds" &&
    recorderMode === SelfRecorderMode.INTERVAL &&
    recorderModeInterval > 0 &&
    recorderModeInterval < recorderModeMinInterval
      ? formatMessage({ id: "form.validations.recorderIntervalBelowUpdate" }, { seconds: recorderModeMinInterval })
      : "";
  const showRecorderModeApplyButton = showRecorderModeInputs && isRecorderModeDraftValid && isRecorderModeDraftDirty;

  const showSlowDataWarning = (() => {
    if (!showRecorderModeInputs || !isRecorderModeActive || !recorderWindowStart || !recorderWindowEnd) return false;

    const upd = user?.profile?.selfRecorderSettings?.upd ?? 0;
    const durationMs = new Date(recorderWindowEnd).getTime() - new Date(recorderWindowStart).getTime();

    return durationMs > 365 * 24 * 60 * 60 * 1000 && upd > 0 && upd < 20;
  })();

  const { fn: setSelfRecorderSettingsFn } = useApiCall(
    async (_api, values: ISetSelfRecorderSettingsDto): Promise<boolean> => {
      await user.runSettingsUpdate(async () => {
        await user.setSelfRecorderSettings(values);
        await user.getProfile();
      });
      return true;
    },
    { success: false },
  );

  const handleRangeTypeChange = (value: RangeType) => {
    changeUserSelectedRangeType(value);
    setIsJournalNavigation(false);
    setJournalTimeRangeHistory([]);
    setManualRangeType(null);
    setSkipNextJournalReload(true);
    setJustRestoredRange(false);
    updateQS({ rangeType: value });
  };

  const handleRecorderModePeriodChange = (e: SelectChangeEvent<string>) => {
    setUserSelectedRecorderModePeriod(e.target.value);
    stopRecorderMode();
  };

  useEffect(() => {
    if (!showRecorderModeInputs || !isJournalTab || !["seconds", "minutes"].includes(userSelectedRecorderModePeriod)) {
      return;
    }

    setUserSelectedRecorderModePeriod("hours");
    stopRecorderMode();
  }, [
    showRecorderModeInputs,
    isJournalTab,
    setUserSelectedRecorderModePeriod,
    stopRecorderMode,
    userSelectedRecorderModePeriod,
  ]);

  const handleIntervalInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "") {
      setRecorderModeInterval(0);
      stopRecorderMode();
      return;
    }
    const num = Number(val);
    if (!Number.isNaN(num)) {
      setRecorderModeInterval(num);
      stopRecorderMode();
    }
  };

  const applyRecorderModeSettings = useCallback(async () => {
    if (!showRecorderModeInputs || !userSelectedRecorderModePeriod) return;
    if (!isRecorderModeDraftValid) return;
    if (isApplying) return;

    const range = SelfRecorderRangeEnum[userSelectedRecorderModePeriod as keyof typeof SelfRecorderRangeEnum];
    if (range === undefined) return;

    setIsApplying(true);

    try {
      const settingsSaved = await setSelfRecorderSettingsFn(undefined, {
        range,
        value: recorderModeInterval,
      });
      if (!settingsSaved) return;

      startRecorderMode({ range, value: recorderModeInterval });
    } finally {
      setIsApplying(false);
    }
  }, [
    recorderModeInterval,
    isRecorderModeDraftValid,
    setSelfRecorderSettingsFn,
    showRecorderModeInputs,
    startRecorderMode,
    userSelectedRecorderModePeriod,
    isApplying,
  ]);

  const handleIntervalInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;

    e.preventDefault();
    void applyRecorderModeSettings();
  };

  const onCalendarClose = useCallback(() => {
    setIsJournalNavigation(false);

    if (userSelectedStartTimestamp && userSelectedEndTimestamp) {
      const nextStartTimestamp = datepickerToFakeUtcISO(userSelectedStartTimestamp);
      const nextEndTimestamp = datepickerToFakeUtcISO(userSelectedEndTimestamp);
      const currentStartTimestamp =
        typeof startTimestamp === "string" ? startTimestamp : datepickerToFakeUtcISO(startTimestamp);
      const currentEndTimestamp =
        typeof endTimestamp === "string" ? endTimestamp : datepickerToFakeUtcISO(endTimestamp);

      if (nextStartTimestamp === currentStartTimestamp && nextEndTimestamp === currentEndTimestamp) return;

      if (tab === "2") {
        setJournalTimeRangeHistory([]);
      }
      updateTimestampsFakeUTC(nextStartTimestamp, nextEndTimestamp);
    }
  }, [
    userSelectedStartTimestamp,
    userSelectedEndTimestamp,
    startTimestamp,
    endTimestamp,
    tab,
    setIsJournalNavigation,
    setJournalTimeRangeHistory,
    updateTimestampsFakeUTC,
  ]);

  useEffect(() => {
    if (userSelectedRangeType !== RangeType.SELECTED_RANGE) return;

    const parsedStart = typeof startTimestamp === "string" ? parseUTCStringAsLocal(startTimestamp) : startTimestamp;
    const parsedEnd = typeof endTimestamp === "string" ? parseUTCStringAsLocal(endTimestamp) : endTimestamp;

    setUserSelectedStartTimestamp(parsedStart);
    setUserSelectedEndTimestamp(parsedEnd);
  }, [startTimestamp, endTimestamp, userSelectedRangeType]);

  useEffect(() => {
    if (rangeType === RangeType.RECORDER_MODE) return;
    if (rangeType !== userSelectedRangeType) return;
    if (
      !user.isAuthenticated() ||
      rangeType === RangeType.SELECTED_RANGE ||
      manualRangeType !== null ||
      skipNextJournalReload ||
      justRestoredRange ||
      !window.localStorage.getItem("user")
    ) {
      return;
    }

    const endTime = new Date();
    let startTime = new Date();

    if (rangeType === RangeType.WORK_DAY) {
      startTime = sub(endTime, { hours: 8 });
    } else if (rangeType === RangeType.FULL_DAY) {
      startTime = sub(endTime, { days: 1 });
    } else if (rangeType === RangeType.WEEK) {
      startTime = sub(endTime, { weeks: 1 });
    } else if (rangeType === RangeType.MONTH) {
      startTime = sub(endTime, { months: 1 });
    } else if (rangeType === RangeType.YEAR) {
      startTime = sub(endTime, { years: 1 });
    }

    updateTimestamps(startTime, endTime);
  }, [
    rangeType,
    userSelectedRangeType,
    manualRangeType,
    skipNextJournalReload,
    justRestoredRange,
    user.isAuthenticated(),
  ]);

  useEffect(() => {
    if (skipNextJournalReload) {
      setSkipNextJournalReload(false);
    }
  }, [skipNextJournalReload]);

  useEffect(() => {
    if (userSelectedRangeType !== RangeType.SELECTED_RANGE) return;
    if (
      userSelectedStartTimestamp &&
      userSelectedEndTimestamp &&
      userSelectedStartTimestamp > userSelectedEndTimestamp
    ) {
      setUserSelectedEndTimestamp(userSelectedStartTimestamp);
    }
  }, [userSelectedStartTimestamp, userSelectedEndTimestamp, userSelectedRangeType]);

  return (
    <Box sx={{ display: "flex", flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mx: { xs: 1, sm: 2 },
          flex: "0 0 auto",
          width:
            userSelectedRangeType === RangeType.SELECTED_RANGE
              ? { xs: "100px", xl: "300px" }
              : { xs: "240px", xl: "300px" },
        }}
      >
        <SvgIcon sx={{ mr: { xs: 1, sm: 2 }, flexShrink: 0 }}>
          <DateTimeIcon />
        </SvgIcon>
        <FormControl
          size="small"
          hiddenLabel
          sx={{
            height: 40,
            borderRadius: 1,
            borderBottom: 0,
            flex: "1 1 auto",
            minWidth: 0,
          }}
        >
          <Select
            id="select-helper"
            value={
              isRecorderModeDisabled && userSelectedRangeType === RangeType.RECORDER_MODE
                ? RangeType.WORK_DAY
                : userSelectedRangeType
            }
            IconComponent={ExpandMore}
            onChange={event => {
              handleRangeTypeChange(event.target.value as RangeType);
            }}
            renderValue={(value): string => formatMessage({ id: `enums.dateTime.${value as string}` })}
            sx={{ width: "100%", fontWeight: 700 }}
            fullWidth
            MenuProps={{
              MenuListProps: {
                sx: {
                  broderRadius: "8px",
                },
              },
            }}
          >
            {Object.keys(RangeType)
              .filter(
                key =>
                  !(isRecorderModeDisabled && RangeType[key as keyof typeof RangeType] === RangeType.RECORDER_MODE),
              )
              .map(key => (
                <MenuItem key={key} value={key}>
                  <Typography fontWeight={700} fontSize={15} lineHeight="32px" color="#363846">
                    <FormattedMessage id={`enums.dateTime.${key}`} />
                  </Typography>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      {showDateRange ? (
        <StyledInputsWrapper sx={{ flex: "0 0 auto", width: "400px" }}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={locale === "RU" ? ru : enGB}
            localeText={
              locale === "RU"
                ? ruRU.components.MuiLocalizationProvider.defaultProps.localeText
                : enUS.components.MuiLocalizationProvider.defaultProps.localeText
            }
          >
            <Box
              sx={{
                display: "flex",
                "&>div.react-datepicker-wrapper": { mr: 0.5 },
                [`& .react-datepicker__month-option,
                    & .react-datepicker__current-month,
                    & .react-datepicker__month-read-view--selected-month
                  `]: {
                  textTransform: "capitalize",
                },
                "& .react-datepicker__navigation--years::before": {
                  borderColor: "#666",
                  borderStyle: "solid",
                  borderWidth: "3px 3px 0 0",
                  content: "''",
                  display: "block",
                  height: "9px",
                  left: "11px",
                  position: "absolute",
                  width: "9px",
                },
                "& .react-datepicker__navigation--years-upcoming::before": {
                  top: "17px",
                  transform: "rotate(315deg)",
                },
                "& .react-datepicker__navigation--years-previous::before": {
                  top: "6px",
                  transform: "rotate(135deg)",
                },
                "& .react-datepicker__current-month": {
                  mb: 1,
                },
                "& .react-datepicker__input-time-container": {
                  alignItems: "center",
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                  m: "8px 0 10px",
                },
                "& .react-datepicker-time__input": {
                  boxSizing: "border-box",
                  font: "inherit",
                  height: "24px",
                  lineHeight: "20px",
                  maxWidth: TIME_INPUT_WITH_SECONDS_WIDTH,
                  minWidth: TIME_INPUT_WITH_SECONDS_WIDTH,
                  px: 0.5,
                  textAlign: "center",
                  width: TIME_INPUT_WITH_SECONDS_WIDTH,
                },
              }}
            >
              <DatePicker
                customInput={<DateRangeInput label={startDateRangeLabel} />}
                selected={userSelectedStartTimestamp}
                dateFormat={DATE_TIME_FORMAT_WITH_SECONDS}
                locale={locale}
                onChange={newValue => {
                  if (newValue) {
                    setUserSelectedStartTimestamp(newValue);
                  }
                  setShouldOpenEndPicker(true);
                }}
                onCalendarClose={() => {
                  if (shouldOpenEndPicker) {
                    setShouldOpenEndPicker(false);
                    setTimeout(() => {
                      endDatePickerRef.current?.setOpen(true);
                    }, 0);
                  }
                }}
                showTimeInput
                shouldCloseOnSelect={false}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                timeInputLabel={timeLabel}
                customTimeInput={
                  <TimeInputWithSeconds
                    ariaLabel={startDateRangeLabel}
                    onDateChange={date => {
                      setUserSelectedStartTimestamp(date);
                      setShouldOpenEndPicker(true);
                    }}
                  />
                }
                minDate={MIN_SELECTABLE_DATE}
                maxDate={new Date()}
                popperPlacement="bottom-start"
              />
              <DatePicker
                ref={endDatePickerRef}
                customInput={<DateRangeInput label={endDateRangeLabel} />}
                locale={locale}
                selected={userSelectedEndTimestamp}
                dateFormat={DATE_TIME_FORMAT_WITH_SECONDS}
                onChange={newValue => {
                  if (newValue) {
                    setUserSelectedEndTimestamp(newValue);
                  }
                }}
                onCalendarClose={onCalendarClose}
                showTimeInput
                shouldCloseOnSelect={false}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                timeInputLabel={timeLabel}
                customTimeInput={
                  <TimeInputWithSeconds ariaLabel={endDateRangeLabel} onDateChange={setUserSelectedEndTimestamp} />
                }
                minDate={userSelectedStartTimestamp || MIN_SELECTABLE_DATE}
                maxDate={new Date()}
                popperPlacement="bottom-end"
              />
            </Box>
          </LocalizationProvider>
        </StyledInputsWrapper>
      ) : null}

      {showRecorderModeInputs && (
        <StyledInputsWrapper sx={{ flex: "2 1 auto", minWidth: 0, px: { xs: 1, sm: 2 } }}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={locale === "RU" ? ru : enGB}
            localeText={
              locale === "RU"
                ? ruRU.components.MuiLocalizationProvider.defaultProps.localeText
                : enUS.components.MuiLocalizationProvider.defaultProps.localeText
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
                minWidth: 0,
              }}
            >
              <Tooltip title={recorderModeValidationMessage} arrow disableInteractive>
                <TextField
                  sx={{ flex: "0 1 70px", minWidth: "70px" }}
                  name={"recorderMode"}
                  value={recorderModeInterval || ""}
                  onChange={e => handleIntervalInputChange(e)}
                  onKeyDown={handleIntervalInputKeyDown}
                  error={!isRecorderModeDraftValid}
                  inputProps={{ min: recorderModeMinInterval, max: 9999 }}
                  variant="outlined"
                  label={formatMessage({ id: "form.labels.period" })}
                  hiddenLabel={false}
                  size="small"
                  required
                />
              </Tooltip>
              {/* <Box sx={{ display: "inline-flex", flex: "0 1 auto", minWidth: 0 }}> */}
              <Box sx={{ display: "inline-flex", flex: "0 0 110px", width: "110px" }}>
                <Select
                  value={selectedRecorderModePeriod}
                  onChange={e => handleRecorderModePeriodChange(e)}
                  size="small"
                  IconComponent={ExpandMore}
                  sx={{ width: "100%" }}
                  MenuProps={{
                    MenuListProps: {
                      sx: {
                        borderRadius: "8px",
                      },
                    },
                  }}
                >
                  {recorderModePeriodOptions.map(key => (
                    <MenuItem key={key} value={key}>
                      <Typography fontWeight={700} fontSize={15} color="#363846">
                        <FormattedMessage id={`form.labels.${key}`} />
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {showRecorderModeApplyButton && (
                <Tooltip title={formatMessage({ id: "form.buttons.apply" })} arrow disableInteractive>
                  <Button
                    type="button"
                    variant="contained"
                    disabled={isApplying}
                    onClick={() => void applyRecorderModeSettings()}
                    sx={{ ml: 1, flexShrink: 0 }}
                  >
                    <CheckOutlined />
                  </Button>
                </Tooltip>
              )}
              {showSlowDataWarning && (
                <Box
                  sx={{
                    flex: "0 1 auto",
                    maxWidth: "300px",
                    minWidth: 0,
                    m: 0.5,
                    bgcolor: "#fff8e1",
                    borderRadius: 1,
                    p: 0.5,
                  }}
                >
                  <Tooltip
                    title={formatMessage({ id: "components.header.warnings.largeWindowShortInterval" })}
                    arrow
                    disableInteractive
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        fontSize: "0.65rem",
                        lineHeight: 1.1,
                        color: "text.secondary",
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        whiteSpace: "normal",
                        maxHeight: "2.2em",
                      }}
                    >
                      {formatMessage({ id: "components.header.warnings.largeWindowShortInterval" })}
                    </Typography>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </LocalizationProvider>
        </StyledInputsWrapper>
      )}
    </Box>
  );
};
